// Lib
import React, { CSSProperties, MutableRefObject, useCallback, useEffect, useRef } from 'react';

// Utils
import { createInertiaScrollDetector } from '../../../../../../canvas/zoom/inertiaScrollDetector';
import { hasCommandModifier, isControlKeyCommand } from '../../../../../../utils/keyboard/keyboardUtility';
import { difference } from '../../../../../../../common/maths/geometry/point';

// Types
import { Point } from '../../../../../../../common/maths/geometry/pointTypes';
import { UseImageModalZoomResult } from '../../../hooks/useImageModalZoom';

const WHEEL_MODES = {
    NONE: 'NONE',
    SCROLL: 'SCROLL',
    ZOOM: 'ZOOM',
} as const;

type UseImageModalV2ZoomInteractionsResult = {
    zoomViewportRef: MutableRefObject<HTMLDivElement | null>;
    onImageMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
    zoomTransformStyle: CSSProperties;
};

/**
 * Connects the V2 image content DOM events to the shared image zoom state.
 */
export const useImageModalV2ZoomInteractions = (
    imageZoom: UseImageModalZoomResult,
): UseImageModalV2ZoomInteractionsResult => {
    const zoomViewportRef = useRef<HTMLDivElement | null>(null);
    const currentWheelModeRef = useRef<(typeof WHEEL_MODES)[keyof typeof WHEEL_MODES]>(WHEEL_MODES.NONE);
    const inertiaScrollDetectorRef = useRef(createInertiaScrollDetector());
    const wheelModeTimeoutRef = useRef<number | null>(null);
    const { endPan, isPanning, isZoomed, panBy, scale, startPan, translation, updatePan, zoomWheelToPoint } = imageZoom;

    const zoomTransformStyle: CSSProperties = {
        transform: `translate3d(${translation.x}px, ${translation.y}px, 0) scale(${scale})`,
        transformOrigin: 'center center',
    };

    const resetWheelMode = useCallback(() => {
        if (wheelModeTimeoutRef.current) {
            window.clearTimeout(wheelModeTimeoutRef.current);
        }

        wheelModeTimeoutRef.current = window.setTimeout(() => {
            currentWheelModeRef.current = WHEEL_MODES.NONE;
        }, 150);
    }, []);

    const getViewportPoint = useCallback((clientPoint: Point): Point | null => {
        const viewportRect = zoomViewportRef.current?.getBoundingClientRect();
        if (!viewportRect) return null;

        return difference({ x: viewportRect.left, y: viewportRect.top }, clientPoint);
    }, []);

    const onImageWheel = useCallback(
        (event: WheelEvent) => {
            inertiaScrollDetectorRef.current.addWheelEvent(event, {
                isTrackpadPinchStyleWheel: isControlKeyCommand(event) && !hasCommandModifier(event),
            });

            const isZoomScroll = hasCommandModifier(event) || isControlKeyCommand(event);

            if (!isZoomScroll) {
                event.preventDefault();
                event.stopPropagation();

                currentWheelModeRef.current = WHEEL_MODES.SCROLL;
                resetWheelMode();

                if (isZoomed) {
                    panBy({ x: -event.deltaX, y: -event.deltaY });
                }

                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (
                inertiaScrollDetectorRef.current.isInertiaScroll() &&
                currentWheelModeRef.current !== WHEEL_MODES.ZOOM
            ) {
                return;
            }

            const anchorPoint = getViewportPoint({ x: event.clientX, y: event.clientY });
            if (!anchorPoint) return;

            currentWheelModeRef.current = WHEEL_MODES.ZOOM;
            resetWheelMode();
            zoomWheelToPoint(anchorPoint, event.deltaY);
        },
        [getViewportPoint, isZoomed, panBy, resetWheelMode, zoomWheelToPoint],
    );

    const onImageMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (event.button !== 0 || !isZoomed) return;

            const didStartPan = startPan({ x: event.clientX, y: event.clientY });
            if (!didStartPan) return;

            event.preventDefault();
            event.stopPropagation();
        },
        [isZoomed, startPan],
    );

    useEffect(() => {
        const zoomViewportElement = zoomViewportRef.current;
        if (!zoomViewportElement) return;

        const abortController = new AbortController();

        zoomViewportElement.addEventListener('wheel', onImageWheel, {
            passive: false,
            signal: abortController.signal,
        });

        return () => {
            abortController.abort();
        };
    }, [onImageWheel]);

    useEffect(() => {
        if (!isPanning) return undefined;

        const onMouseMove = (event: MouseEvent) => {
            updatePan({ x: event.clientX, y: event.clientY });
        };
        const onMouseUp = () => {
            endPan();
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);

        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [endPan, isPanning, updatePan]);

    useEffect(() => {
        return () => {
            if (wheelModeTimeoutRef.current) {
                window.clearTimeout(wheelModeTimeoutRef.current);
            }
        };
    }, []);

    return {
        zoomViewportRef,
        onImageMouseDown,
        zoomTransformStyle,
    };
};
